import React, { useState, useEffect } from "react";
import { Box } from "@mui/joy";
import CameraView from "../../camera/CameraView";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { RecordVideoContent } from "./RecordVideoContent";
import { useVideoPlayer } from "hooks/useVideoPlayer";
import { useFileUpload } from "hooks/useFileUpload";
import ReactGA from "react-ga4";

const RecordVideo = () => {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isCamera = location.state?.isCamera || location.pathname === '/apply/record';

  const { isMuted, setIsMuted, isPlaying, videoRef, toggleMute, togglePlay } =
    useVideoPlayer();

  const { isProcessing, fileInputRef, handleFileChange, handleRequestGallery } =
    useFileUpload(setErrorMessage, setShowError);

  
  useEffect(() => {
    if (location.state?.isMuted !== undefined) {
      setIsMuted(location.state.isMuted);
    } else {
      setIsMuted(true);
    }
  }, [setIsMuted, location.state?.isMuted]);

  useEffect(() => {
    if (videoRef.current) {
      setIsMuted(videoRef.current.muted);
    }
  }, [setIsMuted, videoRef]);

  useEffect(() => {
    if (isCamera) {
      handleOpenCamera();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [isCamera, location.pathname, navigate]);

  const handleOpenCamera = () => {
    setIsCameraOpen(true);
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
  };

  const handleVideoSubmit = (blob: Blob) => {
    setRecordedVideo(blob);
    setIsCameraOpen(false);
    navigate("/apply/review", { state: { camera: true, blob: blob } });
  };

  const handleOpenOptions = () => {
    ReactGA.event({
      category: "Apply Now",
      action: "Apply Now Clicked",
      label: "Apply Now Clicked"
    });
    localStorage.removeItem("applicantData");
    setShowOptions(true);
  };

  const handleCloseOptions = () => {
    setShowOptions(false);
  };

  const handleRequestCamera = () => {
    ReactGA.event({
      category: "Record Video",
      action: "Record Video Clicked",
      label: "Record Video Clicked"
    });
    setShowOptions(false);
    try {
      handleOpenCamera();
    } catch (error) {
      console.error("Error opening camera:", error);
      setErrorMessage(
        "Could not access your camera. Please check permissions and try again."
      );
      setShowError(true);
    }
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleCameraError = (message: string) => {
    setErrorMessage(message);
    setShowError(true);
    handleCloseCamera();
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {!isCameraOpen && !showLoader ? (
        <RecordVideoContent
          introVideo={null}
          showError={showError}
          errorMessage={errorMessage}
          isProcessing={isProcessing}
          showOptions={showOptions}
          isMuted={isMuted}
          isPlaying={isPlaying}
          videoRef={videoRef}
          fileInputRef={fileInputRef}
          onCloseError={handleCloseError}
          onOpenOptions={handleOpenOptions}
          onCloseOptions={handleCloseOptions}
          onRequestCamera={handleRequestCamera}
          onRequestGallery={handleRequestGallery}
          onToggleMute={toggleMute}
          onTogglePlay={togglePlay}
          onFileChange={handleFileChange}
        />
      ) : (
        <CameraView
          onClose={handleCloseCamera}
          onSubmit={handleVideoSubmit}
          onError={handleCameraError}
          setShowLoader={setShowLoader}
          showBackButton={location.pathname === '/apply/record'}
        />
      )}
    </Box>
  );
};
export default RecordVideo;
